import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { selectAuthState } from '@percent/cause-dashboard/context/auth'
import { PageViewSource } from '../useCausesDashboardAnalytics/marketplaceAnalytics.types'
import { useAuthState } from '../useAuthState/useAuthState'
import { useCausesPortalAnalytics } from '../useCausesDashboardAnalytics/useCausesDashboardAnalytics'

const PAGE_VIEW_SOURCE_BY_PATHNAME: Record<string, PageViewSource> = {
  '/home': PageViewSource.Home,
  '/product-offers': PageViewSource.Marketplace,
  '/my-discounts': PageViewSource.MyDiscounts,
  '/savings-calculator': PageViewSource.SavingsCalculator,
  '/profile': PageViewSource.Profile,
  '/users': PageViewSource.Users,
  '/donations': PageViewSource.Donations,
  '/donation-form': PageViewSource.DonationForm,
  '/ai': PageViewSource.AiGateway,
  '/volunteering': PageViewSource.Volunteering,
  '/volunteering/opportunities/create': PageViewSource.VolunteeringCreateOpportunity,
  '/grants': PageViewSource.Grants,
  '/payouts': PageViewSource.Payouts,
  '/my-account': PageViewSource.MyAccount
}

const getPageName = (pathname: string): string | null => {
  const staticPageName = PAGE_VIEW_SOURCE_BY_PATHNAME[pathname]
  if (staticPageName) {
    return staticPageName
  }

  if (pathname.startsWith('/product-offers/')) {
    return null
  }

  if (pathname.startsWith('/volunteering/opportunities/') && pathname.endsWith('/edit')) {
    return PageViewSource.VolunteeringEditOpportunity
  }

  if (pathname.startsWith('/grants/')) {
    return PageViewSource.GrantDetails
  }

  if (pathname.startsWith('/payouts/')) {
    return PageViewSource.PayoutDetails
  }

  return null
}

export function usePageViewTracking() {
  const { pathname } = useLocation()
  const { authState } = useAuthState()
  const { page } = useCausesPortalAnalytics()

  const { loggedIn, userVerified, claimMade } = selectAuthState(authState)
  const isAuthenticated = loggedIn && userVerified && claimMade

  const lastTrackedPathname = useRef<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      lastTrackedPathname.current = null

      return
    }

    if (lastTrackedPathname.current === pathname) {
      return
    }

    lastTrackedPathname.current = pathname

    const pageName = getPageName(pathname)
    if (pageName) {
      page(pageName)
    }
  }, [pathname, isAuthenticated, page])
}
